import type { ISpeedDataMarkResponse } from "@framework/types";

import type {
  IMultiAnalysisDataMark,
  IMultiAnalysisTimeRange,
  IStructureTreePoint,
} from "../../../multi-analysis/types";
import { isTimestampInRange } from "../../../multi-analysis/utils/data-marks";
import { parseMultiAnalysisChartTime } from "../../../multi-analysis/utils/range-frame";

export const SIGNAL_WAVEFORM_MEASURE_TYPE = 6;

export const ORBIT_TIME_TOLERANCE_MS = 180 * 60 * 1000;

export const ORBIT_X_POINT_DIRECTION = 2;

export interface IOrbitChannel {
  idMeasure: number;
  idPoint: number;
  direction: number;
  date: string;
  value: number;
  units: number;
  pickupAngle: number | null;
}

export interface IOrbitControlParameter {
  title: string;
  value: number;
  units: string;
}

export interface IOrbitPair {
  pointTitle: string;
  measureName: string;
  x: IOrbitChannel;
  y: IOrbitChannel;
  controlParameter: IOrbitControlParameter | null;
}

export interface IOrbitPairMark extends IMultiAnalysisDataMark {
  orbitPair: IOrbitPair;
}

export interface IOrbitControlSample {
  date: string;
  value: number;
}

interface IPairOrbitSignalMarksArgs {
  points: readonly IStructureTreePoint[];
  selectedPoints: readonly IStructureTreePoint[];
  rows: readonly ISpeedDataMarkResponse[];
  toleranceMs?: number;
  controlParameter?: { title: string; units: string } | null;
  controlSamples?: readonly IOrbitControlSample[];
  visibleRange?: IMultiAnalysisTimeRange | null;
}

const pointTitle = (point: Pick<IStructureTreePoint, "title">): string => point.title.trim();

type IOrbitSignalRow = ISpeedDataMarkResponse & {
  Key01?: number | null;
};

const settingsKey = (row: IOrbitSignalRow): string =>
  [
    row.Name,
    row.Units,
    row.Param1 ?? "",
    row.Param2 ?? "",
    row.RangeType ?? "",
    row.Resolution ?? "",
    row.Key01 ?? "",
  ].join("\u0000");

export const expandTimestampRange = (
  range: { startTimestamp: string; endTimestamp: string },
  toleranceMs: number,
): { startTimestamp: string; endTimestamp: string } => ({
  startTimestamp: new Date(Date.parse(range.startTimestamp) - toleranceMs).toISOString(),
  endTimestamp: new Date(Date.parse(range.endTimestamp) + toleranceMs).toISOString(),
});

type IOrbitMarkRow = ISpeedDataMarkResponse & { PickupAngle?: number | null };

const channelOf = (row: IOrbitMarkRow, direction: number): IOrbitChannel => ({
  idMeasure: row.idMeasure,
  idPoint: row.idPoint,
  direction,
  date: row.Date,
  value: row.Value1,
  units: row.Units,
  pickupAngle: row.PickupAngle ?? null,
});

const pairKey = (left: ISpeedDataMarkResponse, right: ISpeedDataMarkResponse): string =>
  [left, right]
    .map(row => `${row.idMeasure}@${row.Date}`)
    .sort()
    .join("|");

export const isOrbitPairMark = (mark: IMultiAnalysisDataMark): mark is IOrbitPairMark =>
  "orbitPair" in mark && (mark as IOrbitPairMark).orbitPair != null;

export const collectOrbitQueryPoints = (
  selectedPoints: readonly IStructureTreePoint[],
  allPoints: readonly IStructureTreePoint[],
): IStructureTreePoint[] => {
  const byTitle = new Map<string, IStructureTreePoint[]>();

  allPoints.forEach(point => {
    const title = pointTitle(point);
    const group = byTitle.get(title);
    if (group) group.push(point);
    else byTitle.set(title, [point]);
  });

  const byId = new Map<number, IStructureTreePoint>();

  selectedPoints.forEach(point => {
    byId.set(point.id, point);
    (byTitle.get(pointTitle(point)) ?? []).forEach(candidate => {
      if (candidate.id !== point.id && candidate.direction !== point.direction) {
        byId.set(candidate.id, candidate);
      }
    });
  });

  return Array.from(byId.values());
};

export const findControlValueAt = (samples: readonly IOrbitControlSample[], date: string): number | null => {
  const target = parseMultiAnalysisChartTime(date);
  if (!Number.isFinite(target)) return null;

  const match = samples.reduce<{ time: number; value: number } | null>((best, sample) => {
    const time = parseMultiAnalysisChartTime(sample.date);
    if (!Number.isFinite(time) || time > target) return best;
    if (!best || time >= best.time) return { time, value: sample.value };
    return best;
  }, null);

  return match ? match.value : null;
};

const findPartnerRow = (
  row: ISpeedDataMarkResponse,
  rowTime: number,
  partners: readonly IStructureTreePoint[],
  rowsByPointId: ReadonlyMap<number, ISpeedDataMarkResponse[]>,
  toleranceMs: number,
  visibleRange?: IMultiAnalysisTimeRange | null,
): { row: ISpeedDataMarkResponse; time: number } | null => {
  const settings = settingsKey(row);

  return partners.reduce<{ row: ISpeedDataMarkResponse; time: number; diff: number } | null>((best, partner) => {
    return (rowsByPointId.get(partner.id) ?? []).reduce((partnerBest, candidate) => {
      if (settingsKey(candidate) !== settings) return partnerBest;
      const time = parseMultiAnalysisChartTime(candidate.Date);
      if (!Number.isFinite(time)) return partnerBest;
      const diff = Math.abs(time - rowTime);
      if (diff > toleranceMs) return partnerBest;
      if (visibleRange && !isTimestampInRange(Math.max(rowTime, time), visibleRange)) return partnerBest;
      if (
        !partnerBest ||
        diff < partnerBest.diff ||
        (diff === partnerBest.diff &&
          (candidate.idPoint < partnerBest.row.idPoint ||
            (candidate.idPoint === partnerBest.row.idPoint && candidate.idMeasure < partnerBest.row.idMeasure)))
      ) {
        return { row: candidate, time, diff };
      }
      return partnerBest;
    }, best);
  }, null);
};

const orderChannels = (left: IOrbitChannel, right: IOrbitChannel): { x: IOrbitChannel; y: IOrbitChannel } => {
  if (left.direction === ORBIT_X_POINT_DIRECTION && right.direction !== ORBIT_X_POINT_DIRECTION) {
    return { x: left, y: right };
  }
  if (right.direction === ORBIT_X_POINT_DIRECTION && left.direction !== ORBIT_X_POINT_DIRECTION) {
    return { x: right, y: left };
  }

  return left.direction <= right.direction ? { x: left, y: right } : { x: right, y: left };
};

export const pairOrbitSignalMarks = ({
  points,
  selectedPoints,
  rows,
  toleranceMs = ORBIT_TIME_TOLERANCE_MS,
  controlParameter = null,
  controlSamples = [],
  visibleRange = null,
}: IPairOrbitSignalMarksArgs): IOrbitPairMark[] => {
  if (!selectedPoints.length) return [];

  const pointsById = new Map(points.map(point => [point.id, point]));
  selectedPoints.forEach(point => {
    if (!pointsById.has(point.id)) pointsById.set(point.id, point);
  });

  const partnersByPointId = new Map<number, IStructureTreePoint[]>();
  selectedPoints.forEach(point => {
    const title = pointTitle(point);
    partnersByPointId.set(
      point.id,
      Array.from(pointsById.values()).filter(
        candidate =>
          candidate.id !== point.id && pointTitle(candidate) === title && candidate.direction !== point.direction,
      ),
    );
  });

  // Type was added to the speed marks response together with the Orbit form.
  // Keep compatibility with an API instance that has not been restarted yet:
  // the endpoint still returns waveform rows, but the new field is absent.
  const waveforms = rows.filter(
    row => (row.Type == null || Number(row.Type) === SIGNAL_WAVEFORM_MEASURE_TYPE) && pointsById.has(row.idPoint),
  );
  const rowsByPointId = new Map<number, ISpeedDataMarkResponse[]>();
  waveforms.forEach(row => {
    const group = rowsByPointId.get(row.idPoint);
    if (group) group.push(row);
    else rowsByPointId.set(row.idPoint, [row]);
  });

  const used = new Set<string>();
  const marks: IOrbitPairMark[] = [];

  selectedPoints.forEach(point => {
    const partners = partnersByPointId.get(point.id) ?? [];
    if (!partners.length) return;

    (rowsByPointId.get(point.id) ?? []).forEach(row => {
      const rowTime = parseMultiAnalysisChartTime(row.Date);
      if (!Number.isFinite(rowTime)) return;

      const best = findPartnerRow(row, rowTime, partners, rowsByPointId, toleranceMs, visibleRange);
      if (!best) return;

      const key = pairKey(row, best.row);
      if (used.has(key)) return;
      used.add(key);

      const later = best.time >= rowTime ? best.row : row;
      const pointDirection = pointsById.get(row.idPoint)?.direction ?? 0;
      const partnerDirection = pointsById.get(best.row.idPoint)?.direction ?? 0;
      const channels = orderChannels(channelOf(row, pointDirection), channelOf(best.row, partnerDirection));
      const controlValue = controlParameter ? findControlValueAt(controlSamples, later.Date) : null;

      marks.push({
        key: `orbit:${key}`,
        idMeasure: later.idMeasure,
        idPoint: later.idPoint,
        date: later.Date,
        measureDate: later.Date,
        pointName: pointTitle(point),
        measureName: row.Name,
        measureDescription: row.Description,
        value: later.Value1,
        value2: later.Value2,
        alarmStatus: later.AlarmStatus,
        units: later.Units,
        rotationFrequencyRpm: later.RPM,
        param1: later.Param1,
        param2: later.Param2,
        orbitPair: {
          pointTitle: pointTitle(point),
          measureName: row.Name,
          ...channels,
          controlParameter:
            controlParameter && controlValue != null
              ? { title: controlParameter.title, value: controlValue, units: controlParameter.units }
              : null,
        },
      });
    });
  });

  return marks.sort(
    (left, right) =>
      parseMultiAnalysisChartTime(left.date) - parseMultiAnalysisChartTime(right.date) || left.idPoint - right.idPoint,
  );
};

export const formatOrbitWindowHeader = ({
  pointTitle: title,
  measureName,
  dateLabel,
  controlLabel,
  cursorLabel,
}: {
  pointTitle: string;
  measureName: string;
  dateLabel: string;
  controlLabel: string | null;
  cursorLabel: string | null;
}): string => {
  const base = [title, measureName, dateLabel, controlLabel]
    .filter((part): part is string => Boolean(part))
    .join(" / ");
  return cursorLabel ? `${base} : ${cursorLabel}` : base;
};
